'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import {
    AUTH_STEP_QUERY_PARAMS,
    AUTH_STEPS,
    IAuthFormData,
    IAuthStepQueryParams,
} from '@/features/auth/helpers/auth-constants'
import { useWithLoading } from '@/utils/hooks/use-with-loading'
import { useSignIn, useSignUp } from '@clerk/nextjs/legacy'
import { useFormContext } from 'react-hook-form'
import { toast } from '@/features/toast/toast'
import { AuthInlineNavigation } from '@/features/auth/auth-inline-navigation'
import { useQueryParams } from '@/utils/url/use-query-params'
import { useAuthPageContext } from '@/features/auth/auth-context'
import { StepContainer } from '@/components/steps/step-container'
import { FixedBottomCtaBar } from '@/components/steps/fixed-bottom-cta-bar'
import { makeHandleClerkError } from '@/utils/clerk/handle-clerk-error'
import { getSafeRedirectUrl } from '@/utils/url/get-safe-redirect-url'
import { ATTRIBUTION_QUERY_PARAMS } from '@/features/attribution/utils/constants'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Button } from '@/components/ui/button'
import { completePostAuthNavigation } from '@/features/auth/helpers/complete-post-auth-navigation'

const OTP_LENGTH = 6

export const AuthCodeVerificationStep = () => {
    const [code, setCode] = useState('')
    const [error, setError] = useState<string | null>(null)
    const { isLoading: isVerifying, withLoading: withVerifyLoading } = useWithLoading()
    const { isLoading: isResending, withLoading: withResendLoading } = useWithLoading()
    const { getValues } = useFormContext<IAuthFormData>()
    const { getParam } = useQueryParams<IAuthStepQueryParams>()
    const { stateParams, setCurrentStep } = useAuthPageContext()
    const { signIn, setActive: setSignInActive } = useSignIn()
    const { signUp, setActive: setSignUpActive } = useSignUp()
    const hasAutoSubmitted = useRef(false)

    const authFlow = stateParams[AUTH_STEP_QUERY_PARAMS.AuthFlow] ?? getParam(AUTH_STEP_QUERY_PARAMS.AuthFlow)
    const oauthRedirectUrl = getSafeRedirectUrl(
        stateParams[ATTRIBUTION_QUERY_PARAMS.RedirectUrl] ?? getParam(ATTRIBUTION_QUERY_PARAMS.RedirectUrl) ?? null
    )

    const handleVerifyCode = useCallback(
        async (otpCode: string) => {
            if (otpCode.length !== OTP_LENGTH) return

            setError(null)

            try {
                if (authFlow === 'sign-up') {
                    const result = await signUp?.attemptEmailAddressVerification({
                        code: otpCode,
                    })

                    if (result?.status === 'complete' && result.createdSessionId) {
                        await completePostAuthNavigation(
                            setSignUpActive,
                            result.createdSessionId,
                            oauthRedirectUrl
                        )
                    } else {
                        console.error('Sign-up verification incomplete:', result?.status)
                        setError('Verification incomplete. Please try again.')
                    }
                } else {
                    const result = await signIn?.attemptFirstFactor({
                        strategy: 'email_code',
                        code: otpCode,
                    })

                    if (result?.status === 'complete' && result.createdSessionId) {
                        await completePostAuthNavigation(
                            setSignInActive,
                            result.createdSessionId,
                            oauthRedirectUrl
                        )
                    } else {
                        console.error('Sign-in verification incomplete:', result?.status)
                        setError('Verification incomplete. Please try again.')
                    }
                }
            } catch (err) {
                makeHandleClerkError({
                    shouldRethrow: false,
                    onAllErrors: (errors) => {
                        const firstError = errors[0]
                        if (firstError) {
                            setError(firstError.message)
                        }
                    },
                })(err)
                setCode('')
                hasAutoSubmitted.current = false
            }
        },
        [authFlow, signUp, setSignUpActive, oauthRedirectUrl, signIn, setSignInActive]
    )

    const handleResendCode = withResendLoading(async () => {
        const { email } = getValues()

        if (!authFlow || !email) {
            toast.error('Something went wrong. Please try again.')
            return
        }

        try {
            if (authFlow === 'sign-up') {
                await signUp?.prepareEmailAddressVerification({
                    strategy: 'email_code',
                })
            } else {
                const emailCodeFactor = signIn?.supportedFirstFactors?.find(
                    (factor) => factor.strategy === 'email_code'
                )

                if (emailCodeFactor && 'emailAddressId' in emailCodeFactor) {
                    await signIn?.prepareFirstFactor({
                        strategy: 'email_code',
                        emailAddressId: emailCodeFactor.emailAddressId,
                    })
                }
            }

            toast.success('Verification code sent')
            setCode('')
            setError(null)
            hasAutoSubmitted.current = false
        } catch (err) {
            makeHandleClerkError({
                shouldRethrow: false,
                onAllErrors: (errors) => {
                    const firstError = errors[0]
                    if (firstError) {
                        toast.error(firstError.message)
                    }
                },
            })(err)
        }
    })

    const handleCodeChange = (value: string) => {
        setCode(value)
        setError(null)
        if (value.length < OTP_LENGTH) {
            hasAutoSubmitted.current = false
        }
    }

    useEffect(() => {
        if (code.length === OTP_LENGTH && !hasAutoSubmitted.current && !isVerifying) {
            hasAutoSubmitted.current = true
            withVerifyLoading(() => handleVerifyCode(code))()
        }
    }, [code, isVerifying, withVerifyLoading, handleVerifyCode])

    const email =
        stateParams[AUTH_STEP_QUERY_PARAMS.Email] ?? getParam(AUTH_STEP_QUERY_PARAMS.Email) ?? getValues().email

    const handleBack = () => {
        if (authFlow === 'sign-up') {
            setCurrentStep(AUTH_STEPS.SignUp)
        } else {
            setCurrentStep(AUTH_STEPS.SignIn)
        }
    }

    return (
        <StepContainer className="max-md:pb-28">
            <div className="flex flex-col items-center gap-4">
                {email && (
                    <p className="text-sm text-[#6F6F6F]">
                        Code sent to <span className="font-medium text-[#171717]">{email}</span>
                    </p>
                )}

                <div className="relative">
                    <InputOTP
                        maxLength={OTP_LENGTH}
                        pattern={REGEXP_ONLY_DIGITS}
                        value={code}
                        onChange={handleCodeChange}
                        disabled={isVerifying}
                        autoFocus
                    >
                        <InputOTPGroup>
                            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                                <InputOTPSlot key={index} index={index} className={error ? 'border-destructive' : ''} />
                            ))}
                        </InputOTPGroup>
                    </InputOTP>

                    {isVerifying && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-[10px]">
                            <LoadingSpinner size={20} />
                        </div>
                    )}
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <AuthInlineNavigation
                text="Didn't receive a code?"
                ctaText="Resend code"
                onClick={handleResendCode}
                isLoading={isResending}
                disabled={isResending || isVerifying}
            />

            <FixedBottomCtaBar topMarginClassName="mt-0">
                <Button size="ds2-responsive" variant="ds2-secondary" onClick={handleBack} className="w-full">
                    Back to {authFlow === 'sign-up' ? 'Sign Up' : 'Log in'}
                </Button>
            </FixedBottomCtaBar>
        </StepContainer>
    )
}
